<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <!-- En-tête et Recherche -->
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <h1 class="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <span class="bg-green-100 text-green-700 p-2 rounded-lg">👥</span>
          Liste des Clients
        </h1>

        <div class="relative w-full md:w-96">
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
            </svg>
          </div>
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Rechercher (Nom, Email)..."
            class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm transition duration-150 ease-in-out shadow-sm"
          />
        </div>
      </div>

      <!-- VUE DESKTOP (Tableau) - Caché sur mobile -->
      <div class="hidden md:block overflow-x-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">Client</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comptes</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MDP Admin</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiration</th>
              <th scope="col" class="sticky right-0 z-10 bg-gray-50 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider shadow-sm">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="client in filteredClients" :key="client.Email_Gologin" class="hover:bg-gray-50 transition-colors group">
              <!-- Client Info - Retour à la ligne autorisé -->
              <td class="px-6 py-4">
                <div class="flex items-center">
                  <div class="h-10 w-10 flex-shrink-0 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm">
                    {{ getInitials(client.Nom) }}
                  </div>
                  <div class="ml-4 max-w-xs">
                    <div class="text-sm font-medium text-gray-900 truncate" :title="client.Nom">{{ client.Nom }}</div>
                    <div class="text-sm text-gray-500 break-all">{{ client.Email_Gologin }}</div>
                  </div>
                </div>
              </td>
              
              <!-- Comptes -->
              <td class="px-6 py-4 whitespace-nowrap">
                 <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                   {{ client.Comptes_utilis_s || 0 }} actifs
                 </span>
              </td>

              <!-- MDP -->
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                <span v-if="client.mot_de_passe_admin" class="bg-gray-100 px-2 py-1 rounded border border-gray-200 select-all">
                  {{ client.mot_de_passe_admin }}
                </span>
                <span v-else class="text-gray-400 italic">N/A</span>
              </td>

              <!-- Expiration -->
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div v-if="client.expiration_mot_de_passe" class="flex flex-col">
                  <span>{{ formatDate(client.expiration_mot_de_passe) }}</span>
                  <span class="text-xs text-gray-400" v-if="isExpired(client.expiration_mot_de_passe)">Expiré</span>
                </div>
                <span v-else class="text-gray-400 italic">N/A</span>
              </td>

              <!-- Actions - Sticky Right -->
              <td class="sticky right-0 z-10 bg-white px-6 py-4 whitespace-nowrap text-right text-sm font-medium shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                <div class="flex justify-center items-center gap-3">
                  <button
                    class="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md transition-colors text-xs font-semibold flex items-center gap-1"
                    @click="generateAdminPassword(client)"
                    title="Générer un nouveau mot de passe Admin"
                  >
                    <span>🔑</span> MDP
                  </button>
                  <RowActions 
                    :client="client" 
                    @open-email="openEmailModal"
                    @open-credits="openCreditsModal"
                    @open-password-reset="openPasswordResetModal"
                    @open-client-info="openClientInfoModal"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- VUE MOBILE (Cartes) - Visible sur mobile uniquement -->
      <div class="md:hidden space-y-4">
        <div v-for="client in filteredClients" :key="client.Email_Gologin" class="bg-white shadow rounded-lg p-4 border border-gray-200">
          <div class="flex items-start justify-between">
            <div class="flex items-center gap-3">
              <div class="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm">
                 {{ getInitials(client.Nom) }}
              </div>
              <div>
                <h3 class="text-sm font-bold text-gray-900">{{ client.Nom }}</h3>
                <p class="text-xs text-gray-500 break-all">{{ client.Email_Gologin }}</p>
              </div>
            </div>
            <!-- Menu Actions Mobile -->
            <RowActions 
              :client="client" 
              @open-email="openEmailModal"
              @open-credits="openCreditsModal"
              @open-password-reset="openPasswordResetModal"
              @open-client-info="openClientInfoModal"
            />
          </div>

          <div class="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div class="bg-gray-50 p-2 rounded">
              <span class="text-xs text-gray-500 block">Comptes</span>
              <span class="font-medium text-gray-900">{{ client.Comptes_utilis_s || 0 }}</span>
            </div>
            <div class="bg-gray-50 p-2 rounded">
              <span class="text-xs text-gray-500 block">MDP Admin</span>
              <span v-if="client.mot_de_passe_admin" class="font-mono text-gray-900 break-all select-all">{{ client.mot_de_passe_admin }}</span>
              <span v-else class="text-gray-400 italic">N/A</span>
            </div>
          </div>
          
          <div class="mt-2 bg-gray-50 p-2 rounded">
             <span class="text-xs text-gray-500 block">Expiration</span>
             <span v-if="client.expiration_mot_de_passe" class="text-gray-900">{{ formatDate(client.expiration_mot_de_passe) }}</span>
             <span v-else class="text-gray-400 italic">N/A</span>
          </div>

          <div class="mt-4">
            <button
              class="w-full flex justify-center items-center gap-2 bg-white border border-green-500 text-green-600 hover:bg-green-50 font-medium py-2 px-4 rounded-md transition-colors shadow-sm"
              @click="generateAdminPassword(client)"
            >
              <span>🔑</span> Générer nouveau MDP
            </button>
          </div>
        </div>
      </div>
      
      <!-- État vide -->
      <div v-if="filteredClients.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">Aucun client trouvé</h3>
        <p class="mt-1 text-sm text-gray-500">Essayez de modifier votre recherche.</p>
      </div>

    </div>

    <!-- Modales centralisées -->
    <EmailModal 
      :visible="emailModalVisible" 
      :client="selectedClient" 
      @close="closeEmailModal" 
      @success="handleSuccess"
    />

    <CreditsModal
      :visible="creditsModalVisible"
      :client="selectedClient"
      @close="closeCreditsModal"
      @success="handleSuccess"
    />

    <PasswordResetModal
      :visible="passwordResetModalVisible"
      :client="selectedClient"
      @close="closePasswordResetModal"
    />

    <ClientInfoModal
      :visible="clientInfoModalVisible"
      :client="selectedClient"
      @close="closeClientInfoModal"
    />
  </div>
</template>

<script>
import axios from '../plugins/axios';
import RowActions from '../components/clients/RowActions.vue';
import EmailModal from '../components/clients/EmailModal.vue';
import CreditsModal from '../components/clients/CreditsModal.vue';
import PasswordResetModal from '../components/clients/PasswordResetModal.vue';
import ClientInfoModal from '../components/clients/ClientInfoModal.vue';

export default {
  name: 'ClientList',
  components: {
    RowActions,
    EmailModal,
    CreditsModal,
    PasswordResetModal,
    ClientInfoModal
  },
  data() {
    return {
      clients: [],
      searchQuery: '',
      
      // État des modales
      selectedClient: null,
      emailModalVisible: false,
      creditsModalVisible: false,
      passwordResetModalVisible: false,
      clientInfoModalVisible: false
    };
  },
  computed: {
    filteredClients() {
      const query = this.searchQuery.toLowerCase();
      
      return this.clients.filter(client => {
        const name = (client.Nom || '').toLowerCase();
        const email = (client.Email_Gologin || '').toLowerCase();
        
        return name.includes(query) || email.includes(query);
      });
    }
  },
  methods: {
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },
    isExpired(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },

    // --- Gestion des Modales ---
    openEmailModal(client) {
      this.selectedClient = client;
      this.emailModalVisible = true;
    },
    closeEmailModal() {
      this.emailModalVisible = false;
      this.selectedClient = null;
    },
    
    openCreditsModal(client) {
      this.selectedClient = client;
      this.creditsModalVisible = true;
    },
    closeCreditsModal() {
      this.creditsModalVisible = false;
      this.selectedClient = null;
    },

    openPasswordResetModal(client) {
      this.selectedClient = client;
      this.passwordResetModalVisible = true;
    },
    closePasswordResetModal() {
      this.passwordResetModalVisible = false;
      this.selectedClient = null;
    },

    openClientInfoModal(client) {
      this.selectedClient = client;
      this.clientInfoModalVisible = true;
    },
    closeClientInfoModal() {
      this.clientInfoModalVisible = false;
      this.selectedClient = null;
    },

    handleSuccess(message) {
      alert(message);
      // Optionnel : Rafraîchir si nécessaire
      // this.fetchClients();
    },

    // --- API ---
    async fetchClients() {
      try {
        const response = await axios.get('/admin2/clients');
        this.clients = response.data;
      } catch (error) {
        console.error('Erreur lors de la récupération des clients :', error);
      }
    },

    async generateAdminPassword(client) {
      try {
        const response = await axios.post('/admin2/update-admin-password', {
          email_gologin: client.Email_Gologin
        });
        if (response.data && response.data.success) {
          client.mot_de_passe_admin = response.data.mot_de_passe_admin;
          client.expiration_mot_de_passe = response.data.expiration;
        }
      } catch (error) {
        console.error('Erreur lors de la génération du MDP admin :', error);
        alert(`Erreur : ${error.message}`);
      }
    },

    formatDate(datetime) {
      if (!datetime) return '';
      const date = new Date(datetime);
      return date.toLocaleString('fr-FR', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      }); 
    }
  },
  mounted() {
    this.fetchClients();
  }
};
</script>
